import { Input } from '@/components/ui/input';

export const anatomySections = [
  { title: 'Head, face, and neck', fields: [
    ['Lateral cerebral ventricles', 'Lateral Ventricles'], ['Choroid plexus'], ['Midline falx', 'Falx'],
    ['Cavum septi pellucidi', 'CSP'], ['Cerebellum'], ['Cisterna magna'], ['Upper lip'],
    ['Nuchal fold (mm)', 'Nuchal fold'],
  ], note: 'Nuchal fold measurement: optional at approximately 16–20 weeks.' },
  { title: 'Chest', fields: [
    ['Four-chamber view', '4 Chamber', 'Four Chamber'], ['Heart size'], ['Heart position'],
    ['Left ventricular outflow tract', 'LVOT'], ['Right ventricular outflow tract', 'RVOT'],
    ['Three-vessel view', '3 vessel view'], ['Three-vessel trachea view', '3 vessel trachea view', '3VT'],
  ], note: 'Three-vessel and three-vessel trachea views: if technically feasible.' },
  { title: 'Abdomen', fields: [
    ['Stomach (presence, size, and situs)', 'Stomach'], ['Kidneys'], ['Urinary bladder', 'Bladder'],
    ['Umbilical cord insertion into fetal abdomen', 'Abdominal cord insertion'],
    ['Umbilical cord vessel number', '3 V.Cord', 'Three vessel cord'],
  ] },
  { title: 'Spine', fields: [['Cervical spine'], ['Thoracic spine'], ['Lumbar spine'], ['Sacral spine']] },
  { title: 'Extremities', fields: [['Arms (presence)'], ['Legs (presence)'], ['Hands (presence)'], ['Feet (presence)']] },
  { title: 'External genitalia', fields: [['External genitalia']], note: 'In multiple gestations and when medically indicated.' },
];

// Split source columns, never interpret a finding or turn Seen into Normal.
export function anatomyRow(line: string): [string, string] {
  const tab = line.indexOf('\t');
  if (tab >= 0) return [line.slice(0, tab).trim(), line.slice(tab + 1)];
  const columns = line.trim().match(/^(.*?)\s{2,}(.+)$/);
  if (columns) return [columns[1], columns[2]];
  const category = line.trim().match(/^(.+?)\s+((?:Not seen|Not visualized|Suboptimal|Abnormal|Normal|Seen|Visualized|Yes|No|Vertex|Breech|Cephalic|Transverse|Posterior|Anterior|Fundal)\b.*)$/i);
  return category ? [category[1], category[2]] : [line.trim(), ''];
}
const normalize = (value: string) => value.trim().toLowerCase();

export function AnatomyFields({ value, disabled, onChange }: {
  value: string; disabled: boolean; onChange: (value: string) => void;
}) {
  const lines = value ? value.split('\n') : [];
  const parsed = lines.map(anatomyRow);
  const used = new Set<number>();
  const sections = anatomySections.map(section => ({ ...section, rows: section.fields.map(names => {
    const index = parsed.findIndex(([label], i) => !used.has(i) && names.some(name => normalize(name) === normalize(label)));
    if (index >= 0) used.add(index);
    return { label: names[0], sourceLabel: index >= 0 ? parsed[index][0] : names[0], index, finding: index >= 0 ? parsed[index][1] : '' };
  }) }));
  const extras = parsed.flatMap(([label, finding], index) => used.has(index) || (!label && !finding) || (!finding && /^Fetal (Brain|Heart|Description|Abdomen|Chest|Face|Extremities)$/i.test(label)) ? [] : [{ label, sourceLabel: label, finding, index }]);
  const renderRow = (row: { label: string; sourceLabel: string; finding: string; index: number }, id: string) => (
    <div key={id} className="grid grid-cols-[45%_1fr] items-start gap-3 border-b py-2">
      <label htmlFor={id} className="break-words pt-2 text-sm">{row.label}</label>
      <Input id={id} aria-label={`${row.label} finding`} value={row.finding} disabled={disabled}
        onChange={e => {
          const edited = [...lines];
          const line = `${row.sourceLabel}\t${e.target.value}`;
          if (row.index >= 0) edited[row.index] = line; else edited.push(line);
          onChange(edited.join('\n'));
        }} />
    </div>
  );
  return <div aria-label="Anatomy findings" className="grid grid-cols-1 items-start gap-x-8 sm:grid-cols-2">
    {[sections.slice(0, 2), sections.slice(2)].map((column, columnIndex) => <div key={columnIndex} className="min-w-0">
      <div className="grid grid-cols-[45%_1fr] gap-3 border-b pb-2 text-xs font-semibold text-muted-foreground">
        <span>Anatomy</span><span>Finding / comments</span>
      </div>
      {column.map(section => <section key={section.title} aria-label={section.title} className="py-2">
        <h5 className="py-2 font-semibold">{section.title}</h5>
        {section.note && <p className="mb-2 text-xs text-muted-foreground">{section.note}</p>}
        {section.rows.map((row, index) => renderRow(row, `anatomy-${section.title}-${index}`))}
      </section>)}
    </div>)}
    {extras.length > 0 && <section aria-label="Additional documented anatomy" className="sm:col-span-2">
      <h5 className="py-2 font-semibold">Additional documented anatomy</h5>
      <div className="grid gap-x-8 sm:grid-cols-2">{extras.map(row => renderRow(row, `anatomy-source-${row.index}`))}</div>
    </section>}
  </div>;
}
